import {
    Box,
    VStack,
    HStack,
    Text,
    Slider,
    SliderTrack,
    SliderFilledTrack,
    SliderThumb,
    Switch,
    Select,
    Divider,
    useDisclosure,
    IconButton,
    Tooltip,

    Button,
    ButtonGroup,
} from '@chakra-ui/react';
import { FiSettings, FiMonitor } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { AppDialog } from '../common/AppDialog';
import { RenderSettings, QualityPresets } from '../../rendering/RenderingConstants';
import { useRenderSettingsStore, type QualityPresetName } from '../../state/useRenderSettingsStore';

/**
 * Render Settings Panel
 * 
 * Provides UI for:
 * - Quality presets
 * - Individual effect toggles
 * - Fine-grained settings control
 */
export function RenderSettingsPanel() {
    const { t } = useTranslation();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const localSettings = useRenderSettingsStore((state) => state.settings);
    const setSetting = useRenderSettingsStore((state) => state.setSetting);
    const applyPresetToStore = useRenderSettingsStore((state) => state.applyPreset);

    const updateSetting = <K extends keyof RenderSettings>(
        key: K,
        value: RenderSettings[K]
    ) => {
        setSetting(key, value);
    };

    const applyPreset = (presetName: QualityPresetName) => {
        applyPresetToStore(presetName);
    };

    return (
        <>
            <Tooltip label={t('viewport.render.title')}>
                <IconButton
                    icon={<FiMonitor />}
                    variant="ghost"
                    size="sm"
                    aria-label={t('viewport.render.title')}
                    onClick={onOpen}
                />
            </Tooltip>

            <AppDialog isOpen={isOpen} onClose={onClose} size="md" icon={<FiSettings />} title={t('viewport.render.title')}>
                        <VStack spacing={4} align="stretch">
                            {/* Quality Presets */}
                            <Box>
                                <Text fontSize="xs" fontWeight="semibold" color="var(--color-text-muted)" mb={2}>
                                    {t('viewport.render.qualityPreset')}
                                </Text>                                <ButtonGroup size="sm" isAttached variant="outline">
                                    {(Object.keys(QualityPresets) as QualityPresetName[]).map((preset) => (
                                        <Button
                                            key={preset}
                                            onClick={() => applyPreset(preset)}
                                            textTransform="capitalize"
                                        >
                                            {preset}
                                        </Button>
                                    ))}
                                </ButtonGroup>
                            </Box>

                            <Divider />

                            {/* Shadows */}
                            <Box>
                                <Text fontSize="xs" fontWeight="semibold" color="var(--color-text-muted)" mb={2}>
                                    {t('viewport.render.shadows')}
                                </Text>
                                <VStack spacing={3} align="stretch">
                                    <HStack justify="space-between">
                                        <Text fontSize="sm">{t('viewport.render.enableShadows')}</Text>
                                        <Switch
                                            isChecked={localSettings.shadowsEnabled}
                                            onChange={(e) => updateSetting('shadowsEnabled', e.target.checked)}
                                            colorScheme="blue"
                                            size="sm"
                                        />
                                    </HStack>

                                    <HStack justify="space-between">
                                        <Text fontSize="sm">{t('viewport.render.shadowQuality')}</Text>
                                        <Select
                                            size="sm"
                                            w="120px"
                                            value={localSettings.shadowType}
                                            onChange={(e) => updateSetting('shadowType', e.target.value as any)}
                                            variant="filled"
                                            bg="surface.300"
                                        >
                                            <option value="basic">{t('viewport.render.shadowBasic')}</option>
                                            <option value="pcf">PCF</option>
                                            <option value="pcfsoft">PCF Soft</option>
                                            <option value="vsm">VSM</option>
                                        </Select>
                                    </HStack>
                                </VStack>
                            </Box>

                            <Divider />

                            {/* Post-Processing */}
                            <Box>
                                <Text fontSize="xs" fontWeight="semibold" color="var(--color-text-muted)" mb={2}>
                                    {t('viewport.render.postProcessing')}
                                </Text>
                                <VStack spacing={3} align="stretch">
                                    {/* Bloom */}
                                    <HStack justify="space-between">
                                        <Text fontSize="sm">{t('viewport.render.bloom')}</Text>
                                        <Switch
                                            isChecked={localSettings.bloomEnabled}
                                            onChange={(e) => updateSetting('bloomEnabled', e.target.checked)}
                                            colorScheme="blue"
                                            size="sm"
                                        />
                                    </HStack>
                                    {localSettings.bloomEnabled && (
                                        <Box pl={4}>
                                            <Text fontSize="xs" color="var(--color-text-muted)" mb={1}>{t('viewport.render.intensity')}</Text>
                                            <Slider
                                                value={localSettings.bloomIntensity}
                                                onChange={(v) => updateSetting('bloomIntensity', v)}
                                                min={0}
                                                max={2}
                                                step={0.1}
                                            >
                                                <SliderTrack bg="surface.400">
                                                    <SliderFilledTrack bg="brand.500" />
                                                </SliderTrack>
                                                <SliderThumb />
                                            </Slider>
                                        </Box>
                                    )}

                                    {/* SSAO */}
                                    <HStack justify="space-between">
                                        <Text fontSize="sm">{t('viewport.render.ambientOcclusion')}</Text>
                                        <Switch
                                            isChecked={localSettings.ssaoEnabled}
                                            onChange={(e) => updateSetting('ssaoEnabled', e.target.checked)}
                                            colorScheme="blue"
                                            size="sm"
                                        />
                                    </HStack>
                                    {localSettings.ssaoEnabled && (
                                        <Box pl={4}>
                                            <Text fontSize="xs" color="var(--color-text-muted)" mb={1}>{t('viewport.render.intensity')}</Text>
                                            <Slider
                                                value={localSettings.ssaoIntensity}
                                                onChange={(v) => updateSetting('ssaoIntensity', v)}
                                                min={1}
                                                max={30}
                                                step={1}
                                            >
                                                <SliderTrack bg="surface.400">
                                                    <SliderFilledTrack bg="brand.500" />
                                                </SliderTrack>
                                                <SliderThumb />
                                            </Slider>
                                        </Box>
                                    )}

                                    {/* Outline */}
                                    <HStack justify="space-between">
                                        <Text fontSize="sm">{t('viewport.render.selectionOutline')}</Text>
                                        <Switch
                                            isChecked={localSettings.outlineEnabled}
                                            onChange={(e) => updateSetting('outlineEnabled', e.target.checked)}
                                            colorScheme="blue"
                                            size="sm"
                                        />
                                    </HStack>

                                    {/* Vignette */}
                                    <HStack justify="space-between">
                                        <Text fontSize="sm">{t('viewport.render.vignette')}</Text>
                                        <Switch
                                            isChecked={localSettings.vignetteEnabled}
                                            onChange={(e) => updateSetting('vignetteEnabled', e.target.checked)}
                                            colorScheme="blue"
                                            size="sm"
                                        />
                                    </HStack>
                                </VStack>
                            </Box>

                            <Divider />

                            {/* Tone Mapping */}
                            <Box>
                                <Text fontSize="xs" fontWeight="semibold" color="var(--color-text-muted)" mb={2}>
                                    {t('viewport.render.toneMapping')}
                                </Text>
                                <VStack spacing={3} align="stretch">
                                    <HStack justify="space-between">
                                        <Text fontSize="sm">{t('viewport.render.mode')}</Text>
                                        <Select
                                            size="sm"
                                            w="120px"
                                            value={localSettings.toneMappingMode}
                                            onChange={(e) => updateSetting('toneMappingMode', e.target.value as any)}
                                            variant="filled"
                                            bg="surface.300"
                                        >
                                            <option value="aces">ACES Filmic</option>
                                            <option value="reinhard">Reinhard</option>
                                            <option value="linear">Linear</option>
                                        </Select>
                                    </HStack>

                                    <Box>
                                        <Text fontSize="xs" color="var(--color-text-muted)" mb={1}>
                                            {t('viewport.render.exposure', { value: localSettings.exposure.toFixed(1) })}
                                        </Text>
                                        <Slider
                                            value={localSettings.exposure}
                                            onChange={(v) => updateSetting('exposure', v)}
                                            min={0.1}
                                            max={3}
                                            step={0.1}
                                        >
                                            <SliderTrack bg="surface.400">
                                                <SliderFilledTrack bg="brand.500" />
                                            </SliderTrack>
                                            <SliderThumb />
                                        </Slider>
                                    </Box>
                                </VStack>
                            </Box>
                        </VStack>
            </AppDialog>
        </>
    );
}
